import {
  CopyObjectCommand,
  GetObjectCommand,
  ListObjectsV2Command,
  S3Client,
  type ListObjectsV2CommandInput,
  type ListObjectsV2CommandOutput,
} from '@aws-sdk/client-s3'
import type { AwsCredentialIdentity, AwsCredentialIdentityProvider } from '@aws-sdk/types'

export const UPLOAD_BUCKET = 'oroblanco-applications'

export type S3Io = {
  // Either resolved credentials (admin STS creds) or a provider function (the
  // user's Cognito provider). Passing the provider lets the S3 client refresh
  // the temporary credentials automatically instead of failing once they expire.
  credentials: AwsCredentialIdentity | AwsCredentialIdentityProvider
  region: string
}

// Cognito identity-pool credentials carry an identityId at runtime, but the
// AwsCredentialIdentity type doesn't model it. Fail loudly if it's absent.
export function getIdentityId(credentials: AwsCredentialIdentity): string {
  const id = (credentials as { identityId?: string }).identityId
  if (!id) throw new Error('Credentials are missing identityId')
  return id
}

export async function withS3Client<T>(
  { credentials, region }: S3Io,
  fn: (client: S3Client) => Promise<T>,
): Promise<T> {
  const client = new S3Client({ region, credentials })
  try {
    return await fn(client)
  } finally {
    client.destroy()
  }
}

export async function* paginateList(
  client: S3Client,
  input: Omit<ListObjectsV2CommandInput, 'ContinuationToken'>,
): AsyncGenerator<ListObjectsV2CommandOutput> {
  let token: string | undefined
  do {
    const res = await client.send(
      new ListObjectsV2Command({ ...input, ContinuationToken: token }),
    )
    yield res
    token = res.IsTruncated ? res.NextContinuationToken : undefined
  } while (token)
}

// The distinct ways fetching a JSON object can go, so each caller can decide
// which failures are routine (a missing metadata.json mid-upload), which are
// data corruption worth reporting, and which are fatal.
export type JsonObjectResult =
  | { kind: 'ok'; value: Record<string, unknown> }
  | { kind: 'missing' }
  | { kind: 'invalid-json'; error: SyntaxError }
  | { kind: 'not-object' }

export async function getJsonObject(
  client: S3Client,
  bucket: string,
  key: string,
): Promise<JsonObjectResult> {
  let text: string
  try {
    const res = await client.send(new GetObjectCommand({ Bucket: bucket, Key: key }))
    if (!res.Body) return { kind: 'missing' }
    text = await res.Body.transformToString()
    if (!text.trim()) return { kind: 'missing' }
  } catch (err) {
    if (isNotFoundError(err)) return { kind: 'missing' }
    throw err
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch (err) {
    if (err instanceof SyntaxError) return { kind: 'invalid-json', error: err }
    throw err
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { kind: 'not-object' }
  }
  return { kind: 'ok', value: parsed as Record<string, unknown> }
}

// For callers where the object is a precondition (promote, publish, upgrade):
// any failure is an error with a message that names what was expected.
export async function getRequiredJsonObject(
  client: S3Client,
  bucket: string,
  key: string,
  what: string,
): Promise<Record<string, unknown>> {
  const result = await getJsonObject(client, bucket, key)
  switch (result.kind) {
    case 'ok':
      return result.value
    case 'missing':
      throw new Error(`${what} not found — this version looks incomplete (${key})`)
    case 'invalid-json':
      throw new Error(`${what} is not valid JSON (${key})`)
    case 'not-object':
      throw new Error(`${what} is not a JSON object (${key})`)
  }
}

// Server-side copy within a bucket. The CopySource path must be URL-encoded
// per segment — a correctness detail that should exist in exactly one place.
export async function copyObject(
  client: S3Client,
  bucket: string,
  sourceKey: string,
  destKey: string,
): Promise<void> {
  await client.send(
    new CopyObjectCommand({
      Bucket: bucket,
      CopySource: `${bucket}/${sourceKey.split('/').map(encodeURIComponent).join('/')}`,
      Key: destKey,
      MetadataDirective: 'COPY',
    }),
  )
}

export function isNotFoundError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  const e = err as { name?: string; Code?: string; $metadata?: { httpStatusCode?: number } }
  if (e.name === 'NoSuchKey' || e.name === 'NotFound') return true
  if (e.Code === 'NoSuchKey' || e.Code === 'NotFound') return true
  if (e.$metadata?.httpStatusCode === 404) return true
  return false
}

// A conditional write (If-Match / If-None-Match) lost a race: the object changed
// since we read it. The caller should reload and retry.
export function isPreconditionFailedError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  const e = err as { name?: string; Code?: string; $metadata?: { httpStatusCode?: number } }
  if (e.name === 'PreconditionFailed' || e.Code === 'PreconditionFailed') return true
  if (e.$metadata?.httpStatusCode === 412) return true
  return false
}

// A transient failure (throttling, 5xx, or a network error before any response)
// that is worth retrying with backoff.
export function isRetryableError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false
  const e = err as { name?: string; $metadata?: { httpStatusCode?: number } }
  const status = e.$metadata?.httpStatusCode
  if (typeof status === 'number') return status === 429 || status >= 500
  // No HTTP status usually means the request failed before a response arrived.
  return /Throttl|Timeout|TimedOut|Network|ECONN/i.test(e.name ?? '')
}
